/**
 * „Ist das Fenster schmaler als …?" — der `useNarrow` aus `ui_kits/dashboard/`.
 *
 * **Zuerst der Vorbehalt: Das hier ist die zweite Wahl.** Wo sich eine Anordnung
 * nur in ihren Maßen unterscheidet, gehört die Entscheidung ins Stylesheet —
 * `repeat(auto-fit, minmax(min(100%, 220px), 1fr))` reagiert auf den *Bereich*,
 * in dem es steht, kommt ohne jede Zahl aus und ist schon da, bevor React zum
 * ersten Mal zeichnet. Ein Hook, der das Fenster misst, kann keines von beidem.
 *
 * Er ist nur dort richtig, wo bei schmalem Fenster **anderes DOM** entsteht:
 * eine Seitenleiste, die zur Schublade wird; eine Liste, die zwei ihrer Spalten
 * gar nicht mehr rendert, sondern ihren Inhalt in die erste legt. Solche Fälle
 * lassen sich mit CSS nur nachbauen, indem man beides zeichnet und eines
 * versteckt — und dann steht jede Angabe zweimal in der Seite, einmal davon für
 * eine Sprachausgabe unsichtbar oder eben doppelt.
 *
 * **`matchMedia` und nicht `resize`.** Der Browser wertet die Bedingung selbst
 * aus und meldet sich nur, wenn sie umschlägt — ein `resize`-Zuhörer feuert
 * dagegen bei jedem Pixel und zeichnet die halbe Anwendung neu, während jemand
 * am Fensterrand zieht.
 */
import { useCallback, useSyncExternalStore } from "react";

/**
 * Die Bedingung „schmaler als `width`", als Media Query.
 *
 * **`- 0.02` und nicht `- 1`.** Ein Fenster kann bei einem Zoom oder auf einem
 * Bildschirm mit halben Pixeln 859,5 Pixel breit sein; `max-width: 859px` wäre
 * dort falsch. Die Bereichssyntax `(width < 860px)` sagt dasselbe kürzer und
 * wird von jedem Browser der letzten Jahre verstanden — aber `max-width` von
 * allen, und dieser Ausdruck entscheidet, ob jemand überhaupt an die Navigation
 * kommt.
 */
export function mediaBelow(width: number): string {
  return `(max-width: ${width - 0.02}px)`;
}

/**
 * `true`, solange das Fenster schmaler ist als `width`.
 *
 * Vor der ersten Messung — und überall, wo es kein `window` gibt — ist die
 * Antwort `false`, also die breite Fassung. Das ist die richtige Richtung: Sie
 * zeigt alles. Die schmale versteckt die Navigation hinter einem Knopf, und ein
 * Knopf, der einen Augenblick lang auf einem breiten Bildschirm aufblitzt, sieht
 * aus wie ein Fehler.
 */
export function useNarrow(width: number): boolean {
  const query = mediaBelow(width);

  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}
